import {
	LogicalBusInstanceMetadataSchema,
	LogicalBusResultMetadataSchema,
	LogicalBusValidationErrorSeverity,
	RequirementInstanceResultStatus,
} from '@cadstrom/sigma-engine-shared';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import WarningIcon from '@mui/icons-material/Warning';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { useMemo, useState } from 'react';

import { NetChip } from '#components/net-chip/net-chip.js';
import { AnalysisResultsModuleChip } from '../components/analysis-results-module-chip.js';
import { SimpleSubtableToolbar } from '../components/simple-subtable-toolbar.js';
import { StatusBadge } from '../components/status-badge.js';

import type { RequirementInstanceResult } from '@cadstrom/sigma-engine-shared';
import type { GridColDef, GridRowModel } from '@mui/x-data-grid-premium';

const SEVERITIES = Object.values(LogicalBusValidationErrorSeverity);

const SEVERITY_CONFIG: Record<
	LogicalBusValidationErrorSeverity,
	{ color: 'success' | 'error' | 'warning'; icon: typeof CheckCircleIcon }
> = {
	[LogicalBusValidationErrorSeverity.PASS]: { color: 'success', icon: CheckCircleIcon },
	[LogicalBusValidationErrorSeverity.FAIL]: { color: 'error', icon: ErrorIcon },
	[LogicalBusValidationErrorSeverity.WARNING]: { color: 'warning', icon: WarningIcon },
};

type LogicalBusDetailPanelProps = {
	row: GridRowModel<RequirementInstanceResult>;
	loading?: boolean;
	/**
	 * Optional click-through for module ref-des chip menus. Omit when the panel
	 * is mounted outside the analysis-results grid (e.g. inside a V2 activity
	 * timeline payload) — the menu item is dropped in that case.
	 */
	handleAddModuleFilter?: (moduleReferenceDesignator: string) => void;
};

export function LogicalBusDetailPanel({ row, handleAddModuleFilter, loading = false }: LogicalBusDetailPanelProps) {
	// Parse metadata using sigma-engine-shared schemas
	const instanceMetadataResult = LogicalBusInstanceMetadataSchema.safeParse(row.instanceMetadata);
	const resultMetadataResult = LogicalBusResultMetadataSchema.safeParse(row.resultMetadata);

	const instanceMetadata = instanceMetadataResult.success ? instanceMetadataResult.data : undefined;
	const resultMetadata = resultMetadataResult.success ? resultMetadataResult.data : undefined;

	if (!instanceMetadataResult.success) {
		console.error('[LogicalBusDetailPanel]_InstanceMetadataParseError', {
			error: instanceMetadataResult.error,
			rawData: row.instanceMetadata,
		});
	}

	if (!resultMetadataResult.success) {
		console.error('[LogicalBusDetailPanel]_ResultMetadataParseError', {
			error: resultMetadataResult.error,
			rawData: row.resultMetadata,
		});
	}

	const severityCounts = useMemo(() => {
		const initialCounts: Record<LogicalBusValidationErrorSeverity, number> = {
			[LogicalBusValidationErrorSeverity.PASS]: 0,
			[LogicalBusValidationErrorSeverity.FAIL]: 0,
			[LogicalBusValidationErrorSeverity.WARNING]: 0,
		};

		if (!resultMetadata?.busValidations) {
			return initialCounts;
		}

		return resultMetadata.busValidations.reduce((acc, validation) => {
			const severity = validation.severity;
			acc[severity] = acc[severity] + 1;
			return acc;
		}, initialCounts);
	}, [resultMetadata]);

	const initialSelectedSeverities = useMemo(() => {
		const hasErrorsOrWarnings =
			severityCounts[LogicalBusValidationErrorSeverity.FAIL] > 0 ||
			severityCounts[LogicalBusValidationErrorSeverity.WARNING] > 0;

		if (hasErrorsOrWarnings) {
			// Exclude PASS if there are errors or warnings
			return new Set(SEVERITIES.filter((s) => s !== LogicalBusValidationErrorSeverity.PASS));
		}

		// Include all severities if there are no errors or warnings
		return new Set(SEVERITIES);
	}, [severityCounts]);

	const [selectedSeverities, setSelectedSeverities] =
		useState<Set<LogicalBusValidationErrorSeverity>>(initialSelectedSeverities);

	const toggleSeverity = (severity: LogicalBusValidationErrorSeverity) => {
		setSelectedSeverities((prev) => {
			const next = new Set(prev);
			if (next.has(severity)) {
				next.delete(severity);
			} else {
				next.add(severity);
			}
			return next;
		});
	};

	/**
	 * Validation Details table
	 */
	const validationRows = useMemo(() => {
		return (
			resultMetadata?.busValidations
				.filter((validation) => selectedSeverities.has(validation.severity))
				.map((validation, index) => ({
					id: `${validation.moduleRef ?? 'unknown'}-${index}`,
					...validation,
				})) ?? []
		);
	}, [resultMetadata, selectedSeverities]);

	const validationColumns = useMemo<GridColDef[]>(
		() => [
			{
				field: 'severity',
				headerName: 'Status',
				width: 80,
				renderCell: (params) => {
					const severity = params.value as LogicalBusValidationErrorSeverity;
					const status =
						severity === LogicalBusValidationErrorSeverity.PASS ?
							RequirementInstanceResultStatus.Success
						:	RequirementInstanceResultStatus.Error;
					return (
						<div className='flex h-full items-center gap-1'>
							<StatusBadge status={status} size='small' />
						</div>
					);
				},
			},
			{
				field: 'moduleRef',
				headerName: 'Component',
				width: 120,
				renderCell: (params) => {
					const moduleRef = params.value as string | undefined;
					if (!moduleRef) {
						return null;
					}
					return (
						<AnalysisResultsModuleChip
							moduleReferenceDesignator={moduleRef}
							handleAddModuleFilter={handleAddModuleFilter}
						/>
					);
				},
			},
			{
				field: 'message',
				headerName: 'Message',
				flex: 2,
				minWidth: 300,
			},
		],
		[handleAddModuleFilter],
	);

	/**
	 * Connected components table
	 */
	const { componentRows, componentColumns } = useMemo(() => {
		const components = instanceMetadata?.components ?? [];

		const propertyTypes = new Set<string>();
		for (const component of components) {
			for (const prop of component.displayProperties) {
				propertyTypes.add(prop.type);
			}
		}

		const rows = components.map((component, index) => {
			const row: Record<string, unknown> = {
				id: `${component.moduleRef}-${index}`,
				moduleRef: component.moduleRef,
				moduleLocalFunctionName: component.moduleLocalFunctionName,
			};

			for (const prop of component.displayProperties) {
				row[prop.type] = prop.value;
			}

			return row;
		});

		const columns: GridColDef[] = [
			{
				field: 'moduleRef',
				headerName: 'Component',
				width: 120,
				display: 'flex',
				renderCell: (params) => (
					<AnalysisResultsModuleChip
						moduleReferenceDesignator={params.value as string}
						handleAddModuleFilter={handleAddModuleFilter}
					/>
				),
			},
			{
				field: 'moduleLocalFunctionName',
				headerName: 'Local Function',
				width: 150,
			},
			...Array.from(propertyTypes).map((propType) => ({
				field: propType,
				headerName: propType,
				flex: 1,
				minWidth: 120,
			})),
		];

		return { componentRows: rows, componentColumns: columns };
	}, [instanceMetadata, handleAddModuleFilter]);

	/**
	 * Signal Connections table
	 */

	const { connectionRows, connectionColumns } = useMemo(() => {
		const connections = instanceMetadata?.connections ?? [];

		const groupedBySignal = new Map<string, Array<{ net: string; moduleSignalId: string; moduleRef: string }>>();

		for (const conn of connections) {
			if (!groupedBySignal.has(conn.functionSignal)) {
				groupedBySignal.set(conn.functionSignal, []);
			}
			groupedBySignal.get(conn.functionSignal)?.push({
				net: conn.net,
				moduleSignalId: conn.moduleSignalId,
				moduleRef: conn.moduleRef,
			});
		}

		const rows: Array<{
			id: string;
			functionSignal: string;
			nets: Array<{ net: string; moduleSignalId: string; moduleRef: string }>;
		}> = [];

		for (const [functionSignal, connPairs] of groupedBySignal) {
			rows.push({
				id: functionSignal,
				functionSignal,
				nets: connPairs,
			});
		}

		const columns: GridColDef[] = [
			{
				field: 'functionSignal',
				headerName: 'Signal',
				width: 150,
				align: 'center',
				headerAlign: 'center',
				renderCell: (params) => <div className='flex h-full items-center justify-center'>{params.value as string}</div>,
			},
			{
				field: 'nets',
				headerName: 'Net',
				flex: 1,
				minWidth: 200,
				align: 'center',
				headerAlign: 'center',
				renderCell: (params) => {
					const nets = params.value as Array<{ net: string; moduleSignalId: string; moduleRef: string }>;
					return (
						<div className='flex flex-col items-center gap-2 py-2'>
							{nets.map((item) => (
								<div key={item.net} className='flex items-center'>
									<NetChip net={item.net} />
								</div>
							))}
						</div>
					);
				},
			},
			{
				field: 'moduleSignals',
				headerName: 'Module Signal',
				flex: 1,
				minWidth: 200,
				align: 'center',
				headerAlign: 'center',
				valueGetter: (_value, row: { nets?: Array<{ net: string; moduleSignalId: string; moduleRef: string }> }) =>
					row.nets ?? [],
				renderCell: (params) => {
					const nets = params.value as Array<{ net: string; moduleSignalId: string; moduleRef: string }>;
					return (
						<div className='flex flex-col items-center gap-2 py-2'>
							{nets.map((item) => {
								return (
									<div key={`${item.moduleRef}-${item.moduleSignalId}`} className='flex items-center'>
										<AnalysisResultsModuleChip
											moduleReferenceDesignator={item.moduleRef}
											signalId={item.moduleSignalId}
											handleAddModuleFilter={handleAddModuleFilter}
										/>
									</div>
								);
							})}
						</div>
					);
				},
			},
		];

		return { connectionRows: rows, connectionColumns: columns };
	}, [instanceMetadata, handleAddModuleFilter]);

	if (!instanceMetadata) {
		return null;
	}

	return (
		<div className='flex flex-col gap-2 p-4'>
			<div className='flex items-baseline gap-3'>
				<Tooltip title='Bus type'>
					<Chip label={instanceMetadata.functionClass} />
				</Tooltip>
				<div className='flex flex-wrap gap-2'>
					{SEVERITIES.map((severity) => (
						<Button
							key={severity}
							variant={selectedSeverities.has(severity) ? 'contained' : 'outlined'}
							color={SEVERITY_CONFIG[severity].color}
							onClick={() => toggleSeverity(severity)}
							size='small'
						>
							<span>
								{severity}: {severityCounts[severity]}
							</span>
						</Button>
					))}
				</div>
			</div>

			<div className='mt-2 flex flex-col gap-3'>
				<DataGridPremium rows={validationRows} columns={validationColumns} loading={loading} />
			</div>

			<div className='mt-2 flex flex-col gap-3'>
				<DataGridPremium
					rows={componentRows}
					columns={componentColumns}
					loading={loading}
					showToolbar
					slots={{ toolbar: SimpleSubtableToolbar }}
					slotProps={{
						toolbar: {
							title: 'Connected Components',
							icon: (
								<Tooltip title='Components connected to the bus being validated'>
									<InfoOutlineIcon />
								</Tooltip>
							),
						},
					}}
				/>
			</div>

			<div className='mt-2 flex flex-col gap-3'>
				<DataGridPremium
					rows={connectionRows}
					columns={connectionColumns}
					loading={loading}
					getRowHeight={() => 'auto'}
					showToolbar
					slots={{ toolbar: SimpleSubtableToolbar }}
					slotProps={{
						toolbar: {
							title: 'Signal Connections',
							icon: (
								<Tooltip title='Mapping of bus signals to connected components'>
									<InfoOutlineIcon />
								</Tooltip>
							),
						},
					}}
				/>
			</div>
		</div>
	);
}
